// Ticket desk: member reports become tickets. Staff triage them, talk
// privately (with an on-call agent), and send the member a separate reply.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { ticketsAPI, whatsNewAPI } from '../api/client';
import { useAuth } from '../context/useAuth';
import { isGodModeRole } from '../utils/roles';
import CodingJobPanel from '../components/CodingJobPanel';
import { CodingAgentDot } from '../components/CodingAgentStatus';
import { codingStatus } from '../lib/codingStatus';
import ConfirmModal from '../components/ConfirmModal';
import DecisionLogModal from '../components/DecisionLogModal';
import LogForSomeoneModal from '../components/LogForSomeoneModal';
import MemberEmailPreviewModal from '../components/MemberEmailPreviewModal';
import TicketPersonPicker from '../components/TicketPersonPicker';
import { ScreenshotPicker, ScreenshotStrip } from '../components/TicketScreenshots';
import { pasteScreenshots } from '../lib/screenshots';
import { ticketThread } from '../lib/ticketThread';
import type {
  CodingApproval,
  MemberEmail,
  TicketAccessPerson,
  TicketDetail,
  TicketPerson,
  TicketReport,
  TicketSummary,
} from '../types';
import {
  ArrowLeft,
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  Bot,
  Bug,
  ClipboardList,
  Code2,
  Gavel,
  HelpCircle,
  Image as ImageIcon,
  Inbox,
  Lightbulb,
  Lock,
  Mail,
  Search,
  Send,
  ShieldCheck,
  UserPlus,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';

const KIND_META: Record<TicketSummary['kind'], { icon: typeof Bug; classes: string }> = {
  problem: { icon: Bug, classes: 'bg-red-50 text-red-600' },
  idea: { icon: Lightbulb, classes: 'bg-gold/10 text-gold' },
  question: { icon: HelpCircle, classes: 'bg-navy/10 text-navy' },
};

const STATUS_META: Record<TicketSummary['status'], { label: string; classes: string }> = {
  new: { label: 'New', classes: 'bg-red-50 text-red-600' },
  needs_decision: { label: 'Needs decision', classes: 'bg-purple-50 text-purple-700' },
  accepted: { label: 'Accepted', classes: 'bg-gold/10 text-gold' },
  in_progress: { label: 'In progress', classes: 'bg-navy/10 text-navy' },
  on_staging: { label: 'On staging', classes: 'bg-blue-50 text-blue-700' },
  shipped: { label: 'Shipped', classes: 'bg-green-50 text-green-600' },
  declined: { label: 'Declined', classes: 'bg-gray-100 text-gray-600' },
  closed: { label: 'Closed', classes: 'bg-gray-100 text-gray-600' },
};

// Statuses staff set directly. Needs decision goes through the decision panel;
// Shipped and Declined go through Close, which requires a note.
const OPEN_STATUSES = ['new', 'accepted', 'in_progress', 'on_staging'] as const;

// Moves that can email the reporters. Staff see the exact email first.
const EMAILING_STATUSES: readonly string[] = ['accepted', 'in_progress', 'on_staging'];

interface EmailReview {
  title: string;
  emails: MemberEmail[];
  sendLabel: string;
  quietLabel: string;
  quietNote?: string;
  apply: (notify: boolean) => Promise<TicketDetail>;
  sentMessage: string;
  quietMessage: string;
  onDone?: () => void;
}

const PRIORITY_GROUPS = [
  { id: 'high', label: 'High', dot: 'bg-red-500' },
  { id: 'normal', label: 'Normal', dot: 'bg-navy' },
  { id: 'low', label: 'Low', dot: 'bg-gray-400' },
] as const;

const PRIORITY_FILTERS = [
  { id: 'all', label: 'All', dot: '', active: 'bg-navy border-navy text-white' },
  { id: 'high', label: 'High', dot: 'bg-red-500', active: 'bg-red-600 border-red-600 text-white' },
  { id: 'normal', label: 'Normal', dot: 'bg-navy', active: 'bg-navy border-navy text-white' },
  { id: 'low', label: 'Low', dot: 'bg-gray-400', active: 'bg-gray-600 border-gray-600 text-white' },
] as const;

const PRIORITIES = [
  { id: 'low', label: 'Low' },
  { id: 'normal', label: 'Normal' },
  { id: 'high', label: 'High' },
] as const;

function day(value: string) {
  return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function moment(value: string) {
  return new Date(value).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function callsAgent(text: string) {
  return text.trim().toLowerCase().startsWith('@agent');
}

// The agent answers in the background; the server marks a reply failed after 5 minutes.
const AGENT_POLL_MS = 2500;
const AGENT_POLL_LIMIT_MS = 6 * 60 * 1000;

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1 h-5" aria-hidden>
      {[0, 150, 300].map((delay) => (
        <span
          key={delay}
          className="w-1.5 h-1.5 rounded-full bg-navy animate-bounce"
          style={{ animationDelay: `${delay}ms` }}
        />
      ))}
    </span>
  );
}

function StatusPill({ status }: { status: TicketSummary['status'] }) {
  const meta = STATUS_META[status];
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${meta.classes}`}>
      {meta.label}
    </span>
  );
}

function KindBadge({ kind }: { kind: TicketSummary['kind'] }) {
  const meta = KIND_META[kind];
  const Icon = meta.icon;
  return (
    <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${meta.classes}`}>
      <Icon size={20} />
    </span>
  );
}

function ReportCard({
  report, canDetach, onDetach,
}: { report: TicketReport; canDetach: boolean; onDetach: () => void }) {
  return (
    <div className="flex flex-col items-start">
      <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-gray-100 px-4 py-3 space-y-2">
        {report.logged_by_name && (
          <p className="text-xs text-gray-500">
            Logged by {report.logged_by_name}
            {report.logged_via_display ? ` from a ${report.logged_via_display.toLowerCase()}` : ''}
          </p>
        )}
        {report.message ? (
          <p className="text-sm text-gray-800 whitespace-pre-wrap">{report.message}</p>
        ) : (
          <p className="text-sm text-gray-400 italic">Sent screenshots only</p>
        )}
        {(report.images || []).length > 0 && <ScreenshotStrip images={report.images} size="md" />}
      </div>
      <p className="text-[11px] text-gray-400 mt-1 ml-1">
        {report.user_name} · {moment(report.created_at)}{report.page ? ` · from ${report.page}` : ''}
        {canDetach && (
          <>
            {' · '}
            <button type="button" onClick={onDetach} className="hover:text-red-600 transition">
              Move to own ticket
            </button>
          </>
        )}
      </p>
    </div>
  );
}

export default function Tickets() {
  const { user } = useAuth();
  const isGod = isGodModeRole(user?.role);
  const allowed = Boolean(user?.can_manage_tickets) || isGod;

  const [tab, setTab] = useState<'open' | 'closed' | 'archived'>('open');
  const [query, setQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<'all' | TicketSummary['priority']>('all');
  const [reordering, setReordering] = useState(false);
  const [tickets, setTickets] = useState<TicketSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selected, setSelected] = useState<TicketDetail | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const [approvals, setApprovals] = useState<CodingApproval[]>([]);

  const [accessOpen, setAccessOpen] = useState(false);
  const [emailReview, setEmailReview] = useState<EmailReview | null>(null);
  const decisionAccessRef = useRef<HTMLDivElement>(null);
  const [focusDecisionAccess, setFocusDecisionAccess] = useState(false);
  const [access, setAccess] = useState<TicketAccessPerson[]>([]);
  const [deciders, setDeciders] = useState<TicketPerson[]>([]);
  const [newDecider, setNewDecider] = useState<TicketPerson | null>(null);
  const [logOpen, setLogOpen] = useState(false);
  const [decisionLogOpen, setDecisionLogOpen] = useState(false);

  const [staffNote, setStaffNote] = useState('');
  const [staffFiles, setStaffFiles] = useState<File[]>([]);
  const [memberNote, setMemberNote] = useState('');
  const [closeNote, setCloseNote] = useState('');
  const [closeOutcome, setCloseOutcome] = useState<'shipped' | 'declined'>('shipped');
  const [whatsNewNote, setWhatsNewNote] = useState('');
  const [whatsNewHidden, setWhatsNewHidden] = useState(false);
  const [deciderId, setDeciderId] = useState<number | null>(null);
  const [decisionQuestion, setDecisionQuestion] = useState('');
  const [shareWithDecider, setShareWithDecider] = useState(false);
  const [panel, setPanel] = useState<'reply' | 'close' | 'decision' | null>(null);
  const [detachReport, setDetachReport] = useState<TicketReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [agentThinking, setAgentThinking] = useState(false);
  const [pendingNote, setPendingNote] = useState<string | null>(null);
  const [pendingShots, setPendingShots] = useState(0);
  const [streamingId, setStreamingId] = useState<number | null>(null);
  const [shownChars, setShownChars] = useState(0);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const selectedId = selected?.id;
  const pendingAgent = selected?.staff_messages?.find((item) => item.is_agent && item.pending);
  const pendingAgentId = pendingAgent?.id ?? null;
  const agentWorking = agentThinking || pendingAgentId != null;

  const load = useCallback(async () => {
    try {
      setTickets(await ticketsAPI.list({
        closed: tab === 'closed',
        archived: tab === 'archived',
        q: query,
      }));
    } catch {
      toast.error('Could not load tickets.');
    } finally {
      setIsLoading(false);
    }
  }, [tab, query]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!isGod || selected) return;
    ticketsAPI.codingApprovals().then(setApprovals).catch(() => setApprovals([]));
  }, [isGod, selected]);

  // Notification and Telegram links open a ticket directly: /admin/tickets?ticket=12
  const linkedTicket = searchParams.get('ticket');
  useEffect(() => {
    if (!linkedTicket || !allowed) return;
    ticketsAPI.get(Number(linkedTicket))
      .then(setSelected)
      .catch(() => toast.error('Could not open that ticket.'));
    setSearchParams((params) => {
      params.delete('ticket');
      return params;
    }, { replace: true });
  }, [linkedTicket, allowed, setSearchParams]);

  useEffect(() => {
    if (accessOpen && isGod) {
      ticketsAPI.access().then(setAccess).catch(() => setAccess([]));
    }
  }, [accessOpen, isGod]);

  useEffect(() => {
    if (!accessOpen || !focusDecisionAccess) return;
    decisionAccessRef.current?.scrollIntoView({ block: 'start' });
  }, [accessOpen, focusDecisionAccess, deciders]);

  useEffect(() => {
    if (!allowed || (!accessOpen && panel !== 'decision')) return;
    ticketsAPI.decisionMakers().then(setDeciders).catch(() => setDeciders([]));
  }, [allowed, accessOpen, panel]);

  const openTicket = async (id: number) => {
    try {
      setSelected(await ticketsAPI.get(id));
      setStaffNote('');
      setStaffFiles([]);
      setMemberNote('');
      setCloseNote('');
      setCloseOutcome('shipped');
      setWhatsNewNote('');
      setWhatsNewHidden(false);
      setDecisionQuestion('');
      setShareWithDecider(false);
      setPanel(null);
      setPendingNote(null);
      setStreamingId(null);
      window.scrollTo({ top: 0 });
    } catch {
      toast.error('Could not open that ticket.');
    }
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selected?.staff_messages?.length, agentThinking, pendingNote, shownChars, pendingAgent?.body]);

  useEffect(() => {
    if (pendingAgentId == null || selectedId == null) return;
    const started = Date.now();
    let cancelled = false;
    let timer: number | undefined;
    const poll = async () => {
      try {
        const detail = await ticketsAPI.get(selectedId);
        if (cancelled) return;
        setSelected((current) => (current?.id === selectedId ? detail : current));
        const message = detail.staff_messages?.find((item) => item.id === pendingAgentId);
        if (!message || !message.pending) {
          if (message) {
            setStreamingId(message.id);
            setShownChars(0);
          }
          return;
        }
      } catch {
        // A dropped poll is retried on the next tick.
      }
      if (!cancelled && Date.now() - started < AGENT_POLL_LIMIT_MS) {
        timer = window.setTimeout(poll, AGENT_POLL_MS);
      }
    };
    timer = window.setTimeout(poll, AGENT_POLL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [pendingAgentId, selectedId]);

  useEffect(() => {
    if (streamingId == null || !selected) return;
    const message = selected.staff_messages?.find((item) => item.id === streamingId);
    if (!message || shownChars >= message.body.length) return;
    const timer = window.setTimeout(() => {
      setShownChars((count) => Math.min(count + 4, message.body.length));
    }, 12);
    return () => window.clearTimeout(timer);
  }, [streamingId, shownChars, selected]);

  if (user && !allowed) {
    return <Navigate to="/" replace />;
  }

  const applyDetail = (detail: TicketDetail) => {
    setSelected(detail);
    void load();
  };

  const run = async (action: () => Promise<TicketDetail>, failMessage: string, successMessage?: string) => {
    setBusy(true);
    try {
      applyDetail(await action());
      if (successMessage) toast.success(successMessage);
      return true;
    } catch {
      toast.error(failMessage);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const serverError = (err: unknown) => (err as { response?: { data?: { error?: string } } }).response?.data?.error;

  const changeStatus = async (ticket: TicketDetail, value: string) => {
    const quiet = () => ticketsAPI.update(ticket.id, { status: value });
    if (!EMAILING_STATUSES.includes(value)) {
      await run(quiet, 'Could not update the ticket.');
      return;
    }
    setBusy(true);
    try {
      const preview = await ticketsAPI.emailPreview(ticket.id, { action: 'status', status: value });
      if (preview.emails.length === 0) {
        applyDetail(await quiet());
        return;
      }
      setEmailReview({
        title: `Member sees: ${preview.status_label}`,
        emails: preview.emails,
        sendLabel: 'Send email and update',
        quietLabel: 'Update without emailing',
        quietNote: 'Either way, Your requests shows the new status.',
        apply: (notify) => ticketsAPI.update(ticket.id, { status: value, notify }),
        sentMessage: 'Updated and emailed.',
        quietMessage: 'Updated. No email sent.',
      });
    } catch (err) {
      toast.error(serverError(err) || 'Could not update the ticket.');
    } finally {
      setBusy(false);
    }
  };

  const reviewClose = async (
    ticket: TicketDetail,
    note: string,
    outcome: 'shipped' | 'declined',
    whatsNew: { hidden: boolean; note: string },
  ) => {
    setBusy(true);
    try {
      const preview = await ticketsAPI.emailPreview(ticket.id, { action: 'close', outcome, note });
      setEmailReview({
        title: `${outcome === 'shipped' ? 'Ship' : 'Decline'}: member sees ${preview.status_label}`,
        emails: preview.emails,
        sendLabel: outcome === 'shipped' ? 'Send email and mark shipped' : 'Send email and decline',
        quietLabel: outcome === 'shipped' ? 'Mark shipped without emailing' : 'Decline without emailing',
        quietNote: 'Either way, the note shows in the member\'s Your requests.',
        apply: (notify) => ticketsAPI.close(ticket.id, note, outcome, notify, whatsNew),
        sentMessage: outcome === 'shipped' ? 'Shipped and emailed.' : 'Declined and emailed.',
        quietMessage: outcome === 'shipped' ? 'Shipped. No email sent.' : 'Declined. No email sent.',
        onDone: () => {
          setCloseNote('');
          setPanel(null);
        },
      });
    } catch (err) {
      toast.error(serverError(err) || 'Could not preview the email.');
    } finally {
      setBusy(false);
    }
  };

  const finishReview = async (notify: boolean) => {
    if (!emailReview) return;
    const review = emailReview;
    setBusy(true);
    try {
      applyDetail(await review.apply(notify));
      toast.success(notify ? review.sentMessage : review.quietMessage);
      review.onDone?.();
      setEmailReview(null);
    } catch (err) {
      toast.error(serverError(err) || 'Could not update the ticket.');
    } finally {
      setBusy(false);
    }
  };

  const emailReviewModal = emailReview && (
    <MemberEmailPreviewModal
      title={emailReview.title}
      emails={emailReview.emails}
      busy={busy}
      sendLabel={emailReview.sendLabel}
      quietLabel={emailReview.quietLabel}
      quietNote={emailReview.quietNote}
      onSend={() => void finishReview(true)}
      onQuiet={() => void finishReview(false)}
      onCancel={() => setEmailReview(null)}
    />
  );

  const newCount = tab === 'open' ? tickets.filter((t) => t.status === 'new').length : 0;

  const visibleTickets = priorityFilter === 'all'
    ? tickets
    : tickets.filter((ticket) => ticket.priority === priorityFilter);
  const groups = PRIORITY_GROUPS
    .map((group) => ({ ...group, tickets: visibleTickets.filter((ticket) => ticket.priority === group.id) }))
    .filter((group) => group.tickets.length > 0);

  const moveTicket = async (ticket: TicketSummary, direction: -1 | 1) => {
    const group = tickets.filter((row) => row.priority === ticket.priority);
    const from = group.findIndex((row) => row.id === ticket.id);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= group.length) return;
    const reordered = [...group];
    [reordered[from], reordered[to]] = [reordered[to], reordered[from]];
    const others = tickets.filter((row) => row.priority !== ticket.priority);
    setTickets([...others, ...reordered]);
    try {
      await ticketsAPI.reorder(ticket.priority, reordered.map((row) => row.id));
    } catch {
      toast.error('Could not save the new order.');
      void load();
    }
  };

  if (isLoading && tickets.length === 0 && !selected) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
      </div>
    );
  }

  // ---------- Ticket detail ----------
  if (selected) {
    const closed = ['shipped', 'declined', 'closed'].includes(selected.status);
    const reportImages = [...selected.reports]
      .reverse()
      .flatMap((report) => report.images || []);
    return (
      <div className="max-w-4xl mx-auto space-y-3">
        <button
          type="button"
          onClick={() => { setSelected(null); void load(); }}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-navy transition"
        >
          <ArrowLeft size={16} />
          All tickets
        </button>

        {/* Header: what this is, who reported it, where it stands */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-start gap-4">
            <KindBadge kind={selected.kind} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-lg font-bold text-navy">{selected.title}</h1>
                <StatusPill status={selected.status} />
              </div>
              <p className="text-xs text-gray-400 mt-0.5">Member sees: {selected.status_label}</p>
              <p className="text-sm text-gray-500 mt-0.5">
                {selected.kind_display} · {selected.reporters.join(', ')} · {day(selected.created_at)}
              </p>
            </div>
          </div>

          {reportImages.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <ImageIcon size={14} />
                Member screenshots ({reportImages.length})
              </p>
              <ScreenshotStrip images={reportImages} size="md" />
            </div>
          )}

          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Priority</span>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                {PRIORITIES.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    disabled={busy}
                    onClick={() => void run(
                      () => ticketsAPI.update(selected.id, { priority: p.id }),
                      'Could not change priority.',
                    )}
                    className={`px-3 py-1.5 text-xs font-semibold transition ${
                      selected.priority === p.id
                        ? p.id === 'high' ? 'bg-red-600 text-white' : 'bg-navy text-white'
                        : 'bg-white text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {!selected.archived && (
              <button
                type="button"
                disabled={busy}
                onClick={() => void run(
                  () => ticketsAPI.archive(selected.id),
                  'Could not archive the ticket.',
                  'Archived.',
                )}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold border border-gray-300 text-gray-600 hover:bg-gray-50 transition disabled:opacity-50"
              >
                Archive
              </button>
            )}
            {selected.archived && (
              <button
                type="button"
                disabled={busy}
                onClick={() => void run(
                  () => ticketsAPI.unarchive(selected.id),
                  'Could not restore the ticket.',
                  'Restored.',
                )}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold border border-navy text-navy hover:bg-navy hover:text-white transition disabled:opacity-50"
              >
                Restore
              </button>
            )}
            {!closed && !selected.archived && (
              <>
                <button
                  type="button"
                  onClick={() => setPanel((current) => current === 'reply' ? null : 'reply')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition ${
                    panel === 'reply' ? 'bg-gold text-navy' : 'border border-gold text-navy hover:bg-gold/10'
                  }`}
                >
                  <Mail size={15} />
                  Message member
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (panel !== 'close' && !closeNote.trim() && selected.coding_job?.close_draft) {
                      setCloseNote(selected.coding_job.close_draft);
                    }
                    setPanel((current) => current === 'close' ? null : 'close');
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition ${
                    panel === 'close' ? 'bg-red-600 text-white' : 'border border-red-200 text-red-700 hover:bg-red-50'
                  }`}
                >
                  Close ticket
                </button>
              </>
            )}
            {!closed && !selected.archived && (
              <button
                type="button"
                onClick={() => {
                  if (panel !== 'decision') {
                    setDeciderId(selected.decider?.id ?? null);
                    setDecisionQuestion(selected.decision_question || '');
                  }
                  setPanel((current) => current === 'decision' ? null : 'decision');
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition ${
                  panel === 'decision' ? 'bg-purple-700 text-white' : 'border border-purple-200 text-purple-700 hover:bg-purple-50'
                }`}
              >
                <Gavel size={15} />
                Needs decision
              </button>
            )}
            {!closed && (
              <label className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Status</span>
                <select
                  value={selected.status}
                  disabled={busy}
                  onChange={(event) => void changeStatus(selected, event.target.value)}
                  className="px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm bg-white"
                >
                  {selected.status === 'needs_decision' && (
                    <option value="needs_decision">{STATUS_META.needs_decision.label}</option>
                  )}
                  {OPEN_STATUSES.map((value) => (
                    <option key={value} value={value}>{STATUS_META[value].label}</option>
                  ))}
                </select>
              </label>
            )}
            {closed && (
              <button
                type="button"
                disabled={busy}
                onClick={() => void run(
                  () => ticketsAPI.reopen(selected.id),
                  'Could not reopen the ticket.',
                  'Ticket reopened.',
                )}
                className="px-4 py-1.5 border border-navy text-navy rounded-lg text-sm font-semibold hover:bg-navy hover:text-white transition disabled:opacity-50"
              >
                Reopen
              </button>
            )}
          </div>
        </div>

        <section className="bg-white rounded-2xl px-5 py-4 shadow-sm">
          <h2 className="font-semibold text-navy text-sm">
            Conversation with {selected.reporters.join(', ') || 'the member'}
          </h2>
          <div className="mt-3 space-y-3">
            {ticketThread(selected.reports, selected.member_replies).map((item) => (
              item.type === 'report' ? (
                <ReportCard
                  key={`report-${item.report.id}`}
                  report={item.report}
                  canDetach={selected.reports.length > 1}
                  onDetach={() => setDetachReport(item.report)}
                />
              ) : (
                <div key={`reply-${item.reply.id}`} className="flex flex-col items-end">
                  <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-navy text-white px-4 py-3">
                    <p className="text-sm whitespace-pre-wrap">{item.reply.body}</p>
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1 mr-1">
                    {item.reply.author_name} to the member · {moment(item.reply.created_at)}
                  </p>
                </div>
              )
            ))}
          </div>
        </section>

        {panel === 'reply' && !closed && (
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gold/40">
            <p className="text-xs text-gray-500 mb-2">
              The member sees this in the app and by email. Staff notes stay private.
            </p>
            <textarea
              value={memberNote}
              onChange={(event) => setMemberNote(event.target.value)}
              rows={3}
              placeholder="Answer their question, or tell them what changed…"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:outline-none"
            />
            <button
              type="button"
              disabled={busy || !memberNote.trim()}
              onClick={async () => {
                const note = memberNote;
                if (await run(
                  () => ticketsAPI.reply(selected.id, note),
                  'Could not send that reply.',
                  'Sent to the member.',
                )) {
                  setMemberNote('');
                  setPanel(null);
                }
              }}
              className="mt-2 flex items-center gap-1.5 px-4 py-2 bg-gold text-navy rounded-lg text-sm font-bold hover:opacity-90 transition disabled:opacity-50"
            >
              <Send size={16} />
              Send to member
            </button>
          </div>
        )}

        {panel === 'close' && !closed && (
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-red-100">
            <div className="flex rounded-lg border border-gray-200 overflow-hidden self-start w-fit mb-3">
              {(['shipped', 'declined'] as const).map((outcome) => (
                <button
                  key={outcome}
                  type="button"
                  onClick={() => setCloseOutcome(outcome)}
                  className={`px-3 py-1.5 text-xs font-semibold transition ${
                    closeOutcome === outcome ? 'bg-navy text-white' : 'bg-white text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  {outcome === 'shipped' ? 'Shipped' : 'Declined'}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mb-2">
              {closeOutcome === 'shipped'
                ? 'The member sees "Shipped" with this note: what changed, or the answer.'
                : 'The member sees "Closed" with this reason.'}
            </p>
            <textarea
              value={closeNote}
              onChange={(event) => setCloseNote(event.target.value)}
              rows={3}
              placeholder={closeOutcome === 'shipped' ? 'Closing note the member will see' : 'Reason the member will see'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:outline-none"
            />
            {closeOutcome === 'shipped' && selected.kind !== 'question' && (
              <div className="mt-3 rounded-xl bg-off-white p-3 space-y-2">
                <p className="text-xs text-gray-500">
                  Shipped problems and ideas appear on What's new for every member, with the note above.
                </p>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={whatsNewHidden}
                    onChange={(event) => setWhatsNewHidden(event.target.checked)}
                  />
                  Leave this off What's new
                </label>
                {!whatsNewHidden && (
                  <textarea
                    value={whatsNewNote}
                    onChange={(event) => setWhatsNewNote(event.target.value)}
                    rows={2}
                    placeholder="Optional: reword it for everyone (no names). Leave empty to use the note above."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:outline-none bg-white"
                  />
                )}
              </div>
            )}
            <button
              type="button"
              disabled={busy || !closeNote.trim()}
              onClick={() => void reviewClose(selected, closeNote, closeOutcome, {
                hidden: whatsNewHidden, note: whatsNewNote.trim(),
              })}
              className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition disabled:opacity-50"
            >
              {closeOutcome === 'shipped' ? 'Review email and mark shipped' : 'Review email and decline'}
            </button>
          </div>
        )}

        {panel === 'decision' && !closed && (
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-purple-100 space-y-3">
            <p className="text-xs text-gray-500">
              The decision-maker gets an email and answers in the app. They see this question,
              the member reports, and notes you share with them. Nothing else on the desk.
            </p>
            {deciders.length === 0 ? (
              <p className="text-sm text-gray-500">
                No decision-makers yet. A super admin adds them under Manage access.
              </p>
            ) : (
              <select
                value={deciderId ?? ''}
                onChange={(event) => setDeciderId(event.target.value ? Number(event.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
              >
                <option value="">Choose who decides</option>
                {deciders.map((person) => (
                  <option key={person.id} value={person.id}>{person.name}</option>
                ))}
              </select>
            )}
            <textarea
              value={decisionQuestion}
              onChange={(event) => setDecisionQuestion(event.target.value)}
              rows={3}
              placeholder="What needs deciding? Give the options if there are any."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:outline-none"
            />
            <button
              type="button"
              disabled={busy || !deciderId || !decisionQuestion.trim()}
              onClick={async () => {
                if (!deciderId) return;
                if (await run(
                  () => ticketsAPI.requestDecision(selected.id, deciderId, decisionQuestion.trim()),
                  'Could not ask for a decision.',
                  'Sent for a decision.',
                )) {
                  setPanel(null);
                }
              }}
              className="px-4 py-2 bg-purple-700 text-white rounded-lg text-sm font-semibold hover:bg-purple-800 transition disabled:opacity-50"
            >
              Ask for a decision
            </button>
          </div>
        )}

        {selected.status === 'shipped' && selected.kind !== 'question' && (
          <div className="bg-white rounded-2xl px-5 py-3 shadow-sm flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-gray-600">
              {selected.whats_new_hidden ? "Hidden from What's new." : "Listed on What's new."}
              {' '}
              <Link to="/whats-new" className="text-navy underline">Open What's new</Link> to reword it.
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={() => void run(
                async () => {
                  await whatsNewAPI.updateTicket(selected.id, { hidden: !selected.whats_new_hidden });
                  return ticketsAPI.get(selected.id);
                },
                'Could not update What\'s new.',
              )}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-300 text-gray-600 hover:bg-gray-50 transition disabled:opacity-50"
            >
              {selected.whats_new_hidden ? "Show on What's new" : "Hide from What's new"}
            </button>
          </div>
        )}

        {(selected.decider || selected.decisions.length > 0) && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border-l-4 border-purple-300 space-y-2">
            <h2 className="font-semibold text-navy text-sm flex items-center gap-2">
              <Gavel size={16} className="text-purple-700" />
              Decision
              {selected.decider && (
                <span className="font-normal text-gray-500">
                  · {selected.decider.name}{selected.decision_pending ? ' (waiting)' : ''}
                </span>
              )}
            </h2>
            {selected.decision_question && (
              <p className="text-sm text-gray-600 whitespace-pre-wrap">Q: {selected.decision_question}</p>
            )}
            {selected.decisions.map((entry) => (
              <div key={entry.id} className="rounded-xl bg-purple-50 p-3">
                <p className="text-xs text-gray-500">{entry.decided_by_name} · {moment(entry.created_at)}</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap mt-0.5">{entry.decision}</p>
              </div>
            ))}
          </div>
        )}

        <CodingJobPanel ticket={selected} onChange={applyDetail} />

        <div className="bg-white rounded-2xl shadow-sm border-l-4 border-gray-300 flex flex-col min-h-[62vh]">
          <div className="px-5 pt-4 pb-2">
            <h2 className="font-semibold text-navy flex items-center gap-2">
              <Lock size={16} className="text-gray-400" />
              Staff chat
              <span className="text-xs font-normal text-gray-400">private — the member never sees this</span>
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Call the agent by typing <span className="font-semibold text-navy">@agent</span> and your question, then Post note.
              To hand it to the coding agent, try{' '}
              <span className="font-semibold text-navy">@agent can we get this fixed?</span>
            </p>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3 min-h-[40vh]">
            {(selected.staff_messages || []).length === 0 && pendingNote == null && (
              <p className="text-sm text-gray-400">
                No notes yet. Talk it through here, or type @agent to ask about this ticket.
              </p>
            )}
            {(selected.staff_messages || []).map((message) => {
              const streaming = message.id === streamingId && shownChars < message.body.length;
              const text = message.id === streamingId
                ? message.body.slice(0, shownChars)
                : message.body;
              return (
                <div
                  key={message.id}
                  className={`rounded-xl p-3 ${message.is_agent ? 'bg-navy/5 border border-navy/10' : 'bg-gray-50'}`}
                >
                  <p className="text-xs font-semibold text-gray-500 flex items-center gap-1.5">
                    {message.is_agent && <Bot size={14} className="text-navy" />}
                    {message.author_name}
                    <span className="font-normal text-gray-400">· {moment(message.created_at)}</span>
                    {message.decision_thread && (
                      <span className="font-normal text-purple-700">· decision thread</span>
                    )}
                  </p>
                  {message.pending ? (
                    <div className="mt-2 space-y-1 text-sm text-gray-500">
                      {message.body.split('\n').filter(Boolean).map((line, index) => (
                        <p key={index} className="text-xs text-gray-500">{line}</p>
                      ))}
                      <div className="flex items-center gap-2">
                        <TypingDots />
                        <span>Working on it</span>
                      </div>
                    </div>
                  ) : text && (
                    <p className="text-sm text-gray-800 mt-1 whitespace-pre-wrap">
                      {text}
                      {streaming && <span className="inline-block w-1.5 h-4 ml-0.5 bg-navy/60 align-middle animate-pulse" />}
                    </p>
                  )}
                  {(message.images || []).length > 0 && (
                    <div className="mt-2">
                      <ScreenshotStrip images={message.images} size="md" />
                    </div>
                  )}
                </div>
              );
            })}
            {pendingNote != null && (
              <div className="rounded-xl p-3 bg-gray-50 opacity-80">
                <p className="text-xs font-semibold text-gray-500">You</p>
                {pendingNote.trim() && (
                  <p className="text-sm text-gray-800 mt-1 whitespace-pre-wrap">{pendingNote}</p>
                )}
                {pendingShots > 0 && (
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1.5">
                    <ImageIcon size={13} />
                    Uploading {pendingShots} screenshot{pendingShots === 1 ? '' : 's'}…
                  </p>
                )}
              </div>
            )}
            {agentThinking && (
              <div className="rounded-xl p-3 bg-navy/5 border border-navy/10">
                <p className="text-xs font-semibold text-navy flex items-center gap-1.5">
                  <Bot size={14} />
                  Agent
                </p>
                <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                  <TypingDots />
                  <span>Starting</span>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          <div className="px-5 pb-4 pt-2 border-t border-gray-100 space-y-2">
            <textarea
              value={staffNote}
              onChange={(event) => setStaffNote(event.target.value)}
              onPaste={(event) => pasteScreenshots(event, staffFiles, setStaffFiles)}
              rows={2}
              placeholder="Note to the team, or @agent and a question. Paste a screenshot here too."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:outline-none"
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-3">
                <ScreenshotPicker
                  files={staffFiles}
                  onChange={setStaffFiles}
                  disabled={busy || agentWorking}
                />
                {selected.decider && (
                  <label className="flex items-center gap-1.5 text-xs text-gray-600">
                    <input
                      type="checkbox"
                      checked={shareWithDecider}
                      onChange={(event) => setShareWithDecider(event.target.checked)}
                    />
                    Share with {selected.decider.name}
                  </label>
                )}
              </div>
              <button
                type="button"
                disabled={busy || agentWorking || (!staffNote.trim() && staffFiles.length === 0)}
                onClick={async () => {
                  const note = staffNote.trim();
                  const files = staffFiles;
                  const calling = callsAgent(note);
                  setStaffNote('');
                  setStaffFiles([]);
                  setPendingNote(note);
                  setPendingShots(files.length);
                  if (calling) setAgentThinking(true);
                  setBusy(true);
                  try {
                    const detail = await ticketsAPI.staffChat(
                      selected.id, note, files, shareWithDecider && !calling,
                    );
                    const latestAgent = [...(detail.staff_messages || [])].reverse().find((item) => item.is_agent);
                    applyDetail(detail);
                    if (calling && latestAgent && !latestAgent.pending) {
                      setStreamingId(latestAgent.id);
                      setShownChars(0);
                    }
                  } catch (err) {
                    const serverError = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
                    toast.error(serverError || (calling ? 'The agent could not answer.' : 'Could not post that note.'));
                    setStaffNote(note);
                    setStaffFiles(files);
                  } finally {
                    setPendingNote(null);
                    setPendingShots(0);
                    setAgentThinking(false);
                    setBusy(false);
                  }
                }}
                className="px-4 py-2 bg-navy text-white rounded-lg text-sm font-semibold hover:bg-navy-dark transition disabled:opacity-50"
              >
                Post note
              </button>
            </div>
          </div>
        </div>

        <ConfirmModal
          isOpen={detachReport != null}
          onCancel={() => setDetachReport(null)}
          onConfirm={async () => {
            if (!detachReport) return;
            const report = detachReport;
            setDetachReport(null);
            await run(
              () => ticketsAPI.detach(selected.id, report.id),
              'Could not move that report.',
              'Moved to its own ticket.',
            );
          }}
          title="Move to its own ticket?"
          message={
            detachReport
              ? `${detachReport.user_name}'s report will become a separate ticket. Use this when the agent grouped it wrong.`
              : ''
          }
          confirmLabel="Move it"
          variant="primary"
        />
        {emailReviewModal}
      </div>
    );
  }

  // ---------- Ticket list ----------
  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-navy">Tickets</h1>
            <p className="text-gray-600 text-sm mt-1">
              Member problems, ideas, and questions
              {newCount > 0 && <span className="text-red-600 font-medium"> · {newCount} new</span>}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 self-start">
            <button
              type="button"
              onClick={() => setLogOpen(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition"
            >
              <UserPlus size={16} />
              Log for someone
            </button>
            <button
              type="button"
              onClick={() => setDecisionLogOpen(true)}
              className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
            >
              <ClipboardList size={16} />
              Decision log
            </button>
            {isGod && (
              <button
                type="button"
                onClick={() => setAccessOpen(true)}
                className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
              >
                <ShieldCheck size={16} />
                Manage access
              </button>
            )}
            {isGod && (
              <button
                type="button"
                onClick={() => { setFocusDecisionAccess(true); setAccessOpen(true); }}
                className="flex items-center gap-1.5 px-4 py-2 border border-purple-200 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-50 transition"
              >
                <Gavel size={16} />
                Decision access
              </button>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <div className="flex rounded-lg bg-gray-100 p-1 self-start">
            {(['open', 'closed', 'archived'] as const).map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => { setTab(id); setIsLoading(true); setReordering(false); }}
                className={`px-4 py-1.5 rounded-md text-sm font-semibold capitalize transition ${
                  tab === id ? 'bg-white text-navy shadow-sm' : 'text-gray-500'
                }`}
              >
                {id}
              </button>
            ))}
          </div>
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by name or words in the report"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:outline-none"
            />
          </div>
        </div>

        <div className="mt-3 flex items-start gap-2">
          <div className="flex-1 min-w-0 flex flex-wrap gap-1.5" role="group" aria-label="Filter by priority">
            {PRIORITY_FILTERS.map((filter) => {
              const count = filter.id === 'all'
                ? tickets.length
                : tickets.filter((ticket) => ticket.priority === filter.id).length;
              const active = priorityFilter === filter.id;
              return (
                <button
                  key={filter.id}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setPriorityFilter(filter.id)}
                  className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                    active ? filter.active : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {filter.dot && <span className={`w-2 h-2 rounded-full ${filter.dot}`} />}
                  {filter.label}
                  <span className={active ? 'opacity-80' : 'text-gray-400'}>{count}</span>
                </button>
              );
            })}
          </div>
          {tab === 'open' && (
            <button
              type="button"
              disabled={Boolean(query.trim())}
              title={query.trim() ? 'Clear the search to reorder' : undefined}
              onClick={() => setReordering((value) => !value)}
              className={`ml-auto shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition disabled:opacity-40 ${
                reordering ? 'bg-navy text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <ArrowUpDown size={14} />
              {reordering ? 'Done' : 'Reorder'}
            </button>
          )}
        </div>
        {reordering && (
          <p className="mt-2 text-xs text-gray-500">
            Use the arrows to set the order within each priority. The top ticket is next up.
          </p>
        )}
      </div>

      {approvals.length > 0 && (
        <div className="bg-gold/10 border border-gold/40 rounded-2xl p-4 space-y-2">
          <p className="text-sm font-semibold text-navy flex items-center gap-2">
            <Code2 size={16} />
            {approvals.length === 1 ? '1 coding agent request needs you' : `${approvals.length} coding agent requests need you`}
          </p>
          {approvals.map((item) => (
            <button
              key={item.job_id}
              type="button"
              onClick={() => void openTicket(item.ticket_id)}
              className="w-full text-left bg-white rounded-xl px-3 py-2 text-sm hover:shadow-sm transition flex items-center justify-between gap-3"
            >
              <span className="min-w-0 truncate text-navy font-medium">{item.title}</span>
              <span className="text-xs text-gray-500 whitespace-nowrap">
                {item.state_display}{item.requested_by ? ` · ${item.requested_by}` : ''}
              </span>
            </button>
          ))}
        </div>
      )}

      {visibleTickets.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 shadow-sm text-center">
          <Inbox className="mx-auto text-gray-300" size={48} />
          <h3 className="mt-4 text-lg font-medium text-gray-700">
            {priorityFilter !== 'all' && tickets.length > 0
              ? `No ${priorityFilter} priority tickets here`
              : tab === 'open' ? 'No open tickets' : tab === 'closed' ? 'No closed tickets' : 'Nothing archived'}
          </h3>
          <p className="text-gray-500 mt-1 text-sm">
            {priorityFilter !== 'all' && tickets.length > 0
              ? 'Try another priority, or All.'
              : tab === 'open'
                ? 'Member submissions from Support & Feedback land here.'
                : tab === 'closed'
                  ? 'Tickets you close move here and stay searchable.'
                  : 'Archived tickets stay out of Open and Closed until you restore them.'}
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {groups.map((group) => (
            <section key={group.id} aria-label={`${group.label} priority`}>
              {(priorityFilter === 'all' || reordering) && (
                <h2 className="flex items-center gap-2 px-1 mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <span className={`w-2 h-2 rounded-full ${group.dot}`} />
                  {group.label}
                  <span className="text-gray-400 font-normal normal-case">{group.tickets.length}</span>
                </h2>
              )}
              <div className="space-y-2">
                {group.tickets.map((ticket, index) => (
                  <div key={ticket.id} className="bg-white rounded-2xl shadow-sm hover:shadow-md transition flex items-stretch">
                    <button
                      type="button"
                      onClick={() => void openTicket(ticket.id)}
                      className="min-w-0 flex-1 text-left p-4 flex items-start gap-3 sm:gap-4"
                    >
                      <KindBadge kind={ticket.kind} />
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-navy text-sm line-clamp-2">{ticket.title}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {ticket.reporters.join(', ')} · {ticket.kind_display}
                          {ticket.report_count > 1 ? ` · ${ticket.report_count} reports` : ''}
                          {' · '}{day(ticket.created_at)}
                        </p>
                        {ticket.decision_pending && ticket.decider && (
                          <p className="text-xs text-purple-700 mt-1 flex items-center gap-1.5 font-medium">
                            <Gavel size={13} />
                            Waiting on {ticket.decider.name}
                          </p>
                        )}
                        {ticket.status === 'needs_decision' && !ticket.decision_pending && ticket.decider && (
                          <p className="text-xs text-purple-700 mt-1 flex items-center gap-1.5 font-medium">
                            <Gavel size={13} />
                            {ticket.decider.name} decided
                          </p>
                        )}
                        {ticket.coding_state && (
                          <p className="text-xs text-navy mt-1 flex items-center gap-1.5 font-medium">
                            <CodingAgentDot state={ticket.coding_state} />
                            {codingStatus(ticket.coding_state).headline}
                          </p>
                        )}
                        {ticket.image_count > 0 && (
                          <p className="text-xs text-navy mt-1 flex items-center gap-1">
                            <ImageIcon size={13} />
                            {ticket.image_count} screenshot{ticket.image_count === 1 ? '' : 's'}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1.5 shrink-0">
                        <StatusPill status={ticket.status} />
                        {priorityFilter === 'all' && !reordering && ticket.priority === 'high' && (
                          <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-600">High</span>
                        )}
                      </div>
                    </button>
                    {reordering && (
                      <div className="flex flex-col justify-center border-l border-gray-100 px-1.5">
                        <button
                          type="button"
                          aria-label={`Move ${ticket.title} up`}
                          disabled={index === 0}
                          onClick={() => void moveTicket(ticket, -1)}
                          className="w-10 h-10 flex items-center justify-center rounded-lg text-navy hover:bg-navy/5 disabled:text-gray-200"
                        >
                          <ChevronUp size={20} />
                        </button>
                        <button
                          type="button"
                          aria-label={`Move ${ticket.title} down`}
                          disabled={index === group.tickets.length - 1}
                          onClick={() => void moveTicket(ticket, 1)}
                          className="w-10 h-10 flex items-center justify-center rounded-lg text-navy hover:bg-navy/5 disabled:text-gray-200"
                        >
                          <ChevronDown size={20} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {logOpen && (
        <LogForSomeoneModal
          onClose={() => setLogOpen(false)}
          onLogged={(ticket) => { setLogOpen(false); applyDetail(ticket); }}
        />
      )}
      {decisionLogOpen && (
        <DecisionLogModal
          onClose={() => setDecisionLogOpen(false)}
          onOpenTicket={(id) => { setDecisionLogOpen(false); void openTicket(id); }}
        />
      )}

      {/* Access modal — god only */}
      {accessOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => { setAccessOpen(false); setFocusDecisionAccess(false); }}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-navy">Who can use this desk</h2>
              <button
                type="button"
                onClick={() => { setAccessOpen(false); setFocusDecisionAccess(false); }}
                className="p-1.5 pointer-coarse:p-2.5 pointer-coarse:-m-1 rounded-lg hover:bg-gray-100 transition"
                aria-label="Close"
              >
                <X size={18} className="text-gray-500" />
              </button>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-500">
                Admins you switch on can triage, chat, and reply to members. They do not
                become super admins, and they can't grant access to anyone else.
              </p>
              <div className="mt-4 space-y-2">
                {access.length === 0 && (
                  <p className="text-sm text-gray-400">No other admins on the platform yet.</p>
                )}
                {access.map((person) => (
                  <div key={person.id} className="flex items-center justify-between gap-3 py-1">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-navy truncate">{person.name}</p>
                      <p className="text-xs text-gray-400 truncate">{person.email}</p>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await ticketsAPI.setAccess(person.id, !person.enabled);
                          setAccess((rows) => rows.map((row) => (
                            row.id === person.id ? { ...row, enabled: !person.enabled } : row
                          )));
                        } catch {
                          toast.error('Could not update access.');
                        }
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition whitespace-nowrap ${
                        person.enabled
                          ? 'bg-navy text-white'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {person.enabled ? 'Has access' : 'No access'}
                    </button>
                  </div>
                ))}
              </div>
              <div ref={decisionAccessRef} className="mt-6 pt-5 border-t border-gray-100">
                <h3 className="text-sm font-semibold text-navy flex items-center gap-2">
                  <Gavel size={15} className="text-purple-700" />
                  Decision-makers
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  They answer tickets assigned to them for a decision. They see only those
                  tickets and the decision thread: no staff chat and no coding agent.
                </p>
                <div className="mt-3 space-y-2">
                  {deciders.map((person) => (
                    <div key={person.id} className="flex items-center justify-between gap-3 py-1">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-navy truncate">{person.name}</p>
                        <p className="text-xs text-gray-400 truncate">{person.email}</p>
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await ticketsAPI.setDecisionMaker(person.id, false);
                            setDeciders((rows) => rows.filter((row) => row.id !== person.id));
                          } catch {
                            toast.error('Could not update decision-makers.');
                          }
                        }}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 transition whitespace-nowrap"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <TicketPersonPicker value={newDecider} onChange={setNewDecider} placeholder="Add someone by name or email" />
                  {newDecider && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const added = await ticketsAPI.setDecisionMaker(newDecider.id, true);
                          setDeciders((rows) => (
                            rows.some((row) => row.id === added.id) ? rows : [...rows, added]
                          ));
                          setNewDecider(null);
                        } catch (err) {
                          const serverError = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
                          toast.error(serverError || 'Could not add that person.');
                        }
                      }}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-purple-700 text-white hover:bg-purple-800 transition"
                    >
                      Make {newDecider.name} a decision-maker
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
